<template>
  <div class="flex flex-col gap-10">
    <div id="general" class="flex flex-col gap-6 proxima_nova">
      <h1 class="text-xl text_color font-semibold">General</h1>

      <div class="flex flex-col gap-6 secondary_color text-[13px] font-normal">
        <div class="form_col w-1/2">
          <label for="name">Workspace Name</label>
          <a-input
            id="name"
            v-model:value="workspaceName"
            type="text"
            class="input_medium"
            placeholder="Enter Workspace Name"
            ref="workspaceNameInputRef"
            allow-clear
            @blur="updateWorkspace"
            @keyup.enter="handleEnter"
          />
        </div>
      </div>
    </div>

    <CurrencyView />

    <DefaultCountryCodeView />
  </div>
</template>

<script setup>
import { nextTick, onMounted, ref } from "vue";
import { message } from "ant-design-vue";
import DashboardService from "@/src/api/DashboardService.js";
import eventBus from "@/src/utils/eventBus.js";
import { focusDelay } from "@/src/constants/commonConstants.js";
import { sanitizeInput } from "@/src/utils/utils.js";
import CurrencyView from "@/src/components/currency_components/CurrencyView.vue";
import DefaultCountryCodeView from "@/src/components/currency_components/DefaultCountryCodeView.vue";

const workspaceNameInputRef = ref(null);
const defaultLogo = "path/to/default/logo.png";
const workspaceName = ref("");
const originalWorkspaceName = ref("");
const workspaceLogo = ref(defaultLogo);

const handleEnter = () => {
  workspaceNameInputRef.value?.blur();
};

const updateWorkspace = async () => {
  if (workspaceName.value === originalWorkspaceName.value) {
    return;
  }

  const sanitizedWorkspaceName = sanitizeInput(workspaceName.value);

  if (sanitizedWorkspaceName !== workspaceName.value) {
    message.error("Invalid characters detected in workspace name");
    return;
  }

  if (!sanitizedWorkspaceName.trim()) {
    message.error("Workspace name cannot be empty");
    return;
  }

  try {
    const response = await DashboardService.updateWorkspace({
      name: sanitizedWorkspaceName,
      logo: workspaceLogo.value,
    });

    if (response?.success) {
      originalWorkspaceName.value = sanitizedWorkspaceName;
      eventBus.emit(`refresh-workspace-name`);
      message.success("Workspace updated successfully");
    } else if (response?.error) {
      if (response?.error.includes("Name can't be blank")) {
        message.error("Workspace name is required");
      } else if (response?.error.includes("Name is too short")) {
        message.error("Workspace name must be at least 3 characters long");
      } else {
        message.error("Failed to update workspace");
      }
    }
  } catch (error) {
    message.error("Failed to update workspace due to a network error");
  }
};

const fetchWorkspace = async () => {
  try {
    let response = await DashboardService.fetchCurrentWorkspace();
    if (response?.success) {
      workspaceName.value = response?.workspace?.name;
      originalWorkspaceName.value = response?.workspace?.name;
    } else {
      message.error(response?.error_message);
    }
  } catch (error) {
    message.error("Failed to update workspace");
  }
};

onMounted(() => {
  fetchWorkspace();

  nextTick(() => {
    setTimeout(() => {
      if (workspaceNameInputRef?.value) {
        workspaceNameInputRef.value?.focus();
      }
    }, focusDelay);
  });
});
</script>
